import {
  Dispatch,
  isValidElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import React from "react";
import { Col, Row } from "antd";
import { useRecoilState } from "recoil";
import ClipLoader from "react-spinners/ClipLoader";
import Tile from "./Tile";
import { TileCardDetails } from "../../Interfaces";
import PavilionTurfsDrawer from "../../../components/PavilionTurfsDrawer";
import {
  DISTANCE_RANGE_MAX,
  FILTERS_KEYS,
  SORT_KEYS,
} from "../../../components/FilterAndSort/constants/constants";
import FilterSort from "../../../components/FilterAndSort";
import MobileCard from "../../../components/Cards/MobileCard";
import {
  calculateDistance,
  getTurfDistanceLabel,
  sortTurfByLocation,
} from "../../../utils/calculateDistance";
import dynamic from "next/dynamic";

// Lazy load Carousel to reduce initial bundle size
const Carousel = dynamic(
  () => import("react-responsive-carousel").then((mod) => mod.Carousel),
  { 
    ssr: false, // Client-only carousel
    loading: () => null // No loading state for banner carousel
  }
);
import MobileLocationSelector from "../../../components/Location/MobileLocation";
import { useUserLocation } from "../../../components/Location/LocationContext";
import getUserLocation from "../../../utils/location";
import SelectLocation from "../../../components/SelectLocation";
import { useRouter } from "next/router";
import constants from "../../../utils/constants";
import { TAB_TO_SPORT, filterTurfsBySportTab } from "../../../utils/sportTabs";
import { filtersState } from "./filters/filtersState";
import FiltersInitializer from "../../../components/FilterAndSort/FiltersInitializer";
// OPTIMIZED: Replaced @ant-design/icons with lightweight SVGs
import { WhatsAppIcon, ArrowRightIcon } from "../../../components/Icons";
import { getTurfRouteFromBanner } from "../../../utils/bannerRedirect";

/** City listing promo carousels (mobile + desktop) above turf tiles. Set `true` to show again (Indore only). */
const SHOW_TILE_CONTAINER_OFFER_BANNERS = false;
import { 
  turfProcessor, 
  filterProcessor, 
  sortProcessor,
  getTurfMinPrice,
  SortOrder,
} from "../../../utils/dataProcessing";
import { GET_AREAS_BY_CITY } from "../../../utils/queries";
import { areaNameToSlug, normalizeAreaForMatch } from "../../../utils/turfRoutes";
import { getCanonicalCitySlug } from "../../../config/cityData";

interface TileContainerProp {
  city: string;
  cityId?: string | null;
  search: string;
  setCity: Dispatch<React.SetStateAction<string>>;
  turfData: any[];
  isLoading?: boolean;
  activeTab?: string;
  handleTabChange?: (key: string) => void;
}

const whatsappNumber = "+919238764656"; // Replace with your WhatsApp number in international format
const whatsappUrl = `https://api.whatsapp.com/send?phone=${whatsappNumber}&text=Hello, I have a query regarding the turf. Could you please provide more information about availability and timings?`;

const TileContainer = ({
  city,
  cityId,
  search,
  setCity,
  turfData,
  isLoading,
  activeTab = 'turfs',
  handleTabChange,
}: TileContainerProp) => {
  const [filters, setFilters] = useRecoilState(filtersState);
  const [filteredTurfData, setFilteredTurfData] = useState<TileCardDetails[]>([]);
  const [showNoTurf, setShowNoTurf] = useState(false);
  const [isCityChanging, setIsCityChanging] = useState(false);
  const [showPavilionModal, setShowPavilionModal] = useState(false);
  const [pavilionTurfs, setPavilionTurfs] = useState<any[]>([]);

  const activeSport = TAB_TO_SPORT[activeTab] || '';
  const router = useRouter();
  const [hasRestoredScroll, setHasRestoredScroll] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [areas, setAreas] = useState<{ id: string; name: string }[]>([]);
  const hasRedirectedRef = useRef(false);
  const prevCityRef = useRef<string>("");
  const hasAttemptedLocationRef = useRef(false);

  const routeArea = useMemo(() => {
    const pathFromWindow =
      typeof window !== "undefined"
        ? window.location.pathname
        : (router.asPath || "").split("?")[0];

    const pathParts = pathFromWindow.split("/").filter(Boolean);
    // Only extract area from exact listing URLs (/city/area/slug), not detail pages.
    if (pathParts.length !== 3 || pathParts[1]?.toLowerCase() !== "area") {
      return null;
    }

    const encodedArea = pathParts[2];
    try {
      return decodeURIComponent(encodedArea);
    } catch {
      return encodedArea;
    }
  }, [router.asPath]);

  // Pagination state - reduces DOM nodes for better performance
  // OPTIMIZED: Reduced for faster TBT (Time to Interactive)
  const INITIAL_DISPLAY_COUNT = 3; // Show 3 cards initially for faster load
  const LOAD_MORE_COUNT = 6; // Load 6 more when scrolling
  const [displayCount, setDisplayCount] = useState(INITIAL_DISPLAY_COUNT);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Infinite scroll - load more when sentinel comes into view
  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel) return;

    // Determine total items based on active tab
    const totalItems = getFilteredDataForTab().length;
    const hasMore = displayCount < totalItems;

    if (!hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !isLoadingMore) {
          setIsLoadingMore(true);
          // Small delay for smooth UX
          setTimeout(() => {
            setDisplayCount(prev => prev + LOAD_MORE_COUNT);
            setIsLoadingMore(false);
          }, 300);
        }
      },
      {
        root: null,
        rootMargin: '200px', // Load 200px before reaching the end
        threshold: 0.1,
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [displayCount, filteredTurfData.length, activeTab, isLoadingMore]);

  // Reset display count when city or activeTab changes
  useEffect(() => {
    setDisplayCount(INITIAL_DISPLAY_COUNT);
  }, [city, activeTab]);

  // Ensure hydration is complete
  useEffect(() => {
    setIsHydrated(true);
  }, []);
  // Clear filtered data immediately when city changes
  useEffect(() => {
    setIsCityChanging(true);
    setFilteredTurfData([]);
    setShowNoTurf(false);
    hasRedirectedRef.current = false; // Reset redirect flag when city changes
    
    // Clear the city changing flag after a short delay to allow data to load
    const timeoutId = setTimeout(() => {
      setIsCityChanging(false);
    }, 2000); // 2 seconds should be enough for data to load
    
    return () => clearTimeout(timeoutId);
  }, [city]);

  // Filter turfData by active sport tab
  const getFilteredDataForTab = () => {
    return filterTurfsBySportTab(filteredTurfData, activeTab);
  };

  // Fetch areas when city changes
  useEffect(() => {
    const cityChanged = prevCityRef.current !== city;

    // Reset only when city actually changes to avoid update loops
    if (cityChanged) {
      setAreas([]);
      setFilters((prev) => {
        if (prev.selectedArea === null) return prev;
        return {
          ...prev,
          selectedArea: null,
        };
      });
      prevCityRef.current = city;
    }
    
    const fetchAreas = async () => {
      if (!city || typeof window === 'undefined') return;
      
      try {
        // Get city ID from cityData
        const { getCityData } = await import('../../../config/cityData');
        const cityData = getCityData(city);
        const resolvedCityId = cityId || cityData?.id;
        
        if (!resolvedCityId || resolvedCityId.startsWith('dynamic-')) {
          // Try to get areas from turfData if cityId not found
          const uniqueAreas = new Map();
          turfData?.forEach((turf: any) => {
            if (turf.data?.areaName && turf.data?.areaId) {
              uniqueAreas.set(turf.data.areaId, { id: turf.data.areaId, name: turf.data.areaName });
            }
          });
          setAreas(Array.from(uniqueAreas.values()));
          return;
        }

        const response = await fetch(`${process.env.NEXT_PUBLIC_TURF_URL}/graphql`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            query: GET_AREAS_BY_CITY,
            variables: { cityId: resolvedCityId },
          }),
        });
        
        const result = await response.json();
        if (result?.data?.areasByCity && result.data.areasByCity.length > 0) {
          setAreas(result.data.areasByCity);
        }
      } catch (error) {
        console.error('Error fetching areas:', error);
        // Fallback: extract unique areas from turfData
        const uniqueAreas = new Map();
        turfData?.forEach((turf: any) => {
          if (turf.data?.areaName && turf.data?.areaId) {
            uniqueAreas.set(turf.data.areaId, { id: turf.data.areaId, name: turf.data.areaName });
          }
        });
        setAreas(Array.from(uniqueAreas.values()));
      }
    };

    if (city) {
      fetchAreas();
    }
  }, [city, cityId, turfData, setFilters]);

  // Sync selected area from URL when on /[city]/area/[area]
  useEffect(() => {
    if (!router.isReady) return;
    if (!routeArea) return;

    const hasLoadedAreas = areas.length > 0;
    // Wait until area list is loaded so we can preserve canonical case
    // and avoid syncing lowercase route value into filter state.
    if (!hasLoadedAreas) return;

    const matchedArea = areas.find(
      (area) => normalizeAreaForMatch(area.name) === normalizeAreaForMatch(routeArea)
    );
    const isRouteAreaValid = !!matchedArea;

    // If areas are loaded and route area is invalid for this city,
    // do not keep syncing invalid value into filter state.
    if (hasLoadedAreas && !isRouteAreaValid) {
      if (filters.selectedArea !== null) {
        setFilters((prev) => ({
          ...prev,
          selectedArea: null,
        }));
      }
      return;
    }

    const normalizedRouteArea = matchedArea?.name ?? null;

    if (normalizedRouteArea && filters.selectedArea !== normalizedRouteArea) {
      setFilters((prev) => ({
        ...prev,
        selectedArea: normalizedRouteArea,
      }));
    }
  }, [router.isReady, router.pathname, router.asPath, routeArea, setFilters, areas]);

  // Keep route in sync with selected area filter
  useEffect(() => {
    if (!router.isReady || !city) return;
    if (city === 'noData') return;

    const isCityOrAreaRoute =
      router.pathname === '/[city]' || router.pathname === '/[city]/area/[area]';
    if (!isCityOrAreaRoute) return;

    const citySlug = getCanonicalCitySlug(city);
    const areaSlug = filters.selectedArea
      ? areaNameToSlug(filters.selectedArea)
      : null;
    const targetPath = filters.selectedArea
      ? `/${citySlug}/area/${areaSlug}`
      : `/${citySlug}`;

    const currentPath =
      typeof window !== 'undefined'
        ? window.location.pathname
        : (router.asPath || '').split('?')[0];
    const currentSearch =
      typeof window !== 'undefined' ? window.location.search : '';

    if (currentPath !== targetPath) {
      window.history.replaceState(
        window.history.state,
        '',
        `${targetPath}${currentSearch}`
      );
    }
  }, [router.isReady, router.pathname, city, filters.selectedArea]);

  // Handle filtering and processing data - will be moved after function declarations


  const [visible, setVisible] = useState(false);
  const { userLocation, setUserLocation } = useUserLocation();

  function comparePrice(
    a: TileCardDetails,
    b: TileCardDetails,
    sortOrder: SortOrder
  ): number {
    // Helper function to get the minimum price from the areaPrice array
    const getMinPrice = (turf: TileCardDetails): number => {
      let minPrice = Infinity;
      
      // Add null checks for data and areaPrice
      if (!turf?.data?.areaPrice || !Array.isArray(turf.data.areaPrice)) {
        return Infinity;
      }
      
      turf.data.areaPrice.forEach((area) => {
        // Add null checks for area and price properties
        if (area?.price?.weekday?.regularHours !== undefined) {
          const minAreaPrice = Math.min(
            area.price.weekday.regularHours,
            area.price.weekday.regularHours
            // area.price.weekday?.happyHours?.price || 0,
            // area.price.weekend?.happyHours?.price || 0
          );
          if (minAreaPrice < minPrice) {
            minPrice = minAreaPrice;
          }
        }
      });
      return minPrice;
    };

    const priceA = getMinPrice(a);
    const priceB = getMinPrice(b);

    if (sortOrder === SortOrder.ASC) {
      return priceA - priceB;
    } else {
      return priceB - priceA;
    }
  }
  function compareHeight(
    a: TileCardDetails,
    b: TileCardDetails,
    sortOrder: SortOrder
  ): number {
    // Helper function to get the maximum height from the areaPrice array
    const getMaxHeight = (turf: TileCardDetails): number => {
      let maxHeight = -1;
      
      // Add null checks for data and areaPrice
      if (!turf?.data?.areaPrice || !Array.isArray(turf.data.areaPrice)) {
        return -1;
      }
      
      turf.data.areaPrice.forEach((area: any) => {
        // Add null checks for area and pitchDimensions properties
        if (Number(area?.pitchDimensions?.height) > 0) {
          const height = Number(area.pitchDimensions.height);
          if (height > maxHeight) {
            maxHeight = height;
          }
        }
      });
      return maxHeight;
    };
    const heightA = getMaxHeight(a);
    const heightB = getMaxHeight(b);
    // Sort in descending order
    if (sortOrder === SortOrder.DESC) {
      return heightB - heightA;
    } else {
      return heightA - heightB; // This will keep heights sorted in ascending order if needed
    }
  }
  function sortByRating(data: TileCardDetails[], sortOrder: SortOrder) {
    if (sortOrder === SortOrder.ASC) {
      return data.sort(
        (a, b) => Number(a?.data?.starRating || 0) - Number(b?.data?.starRating || 0)
      );
    } else {
      return data.sort(
        (a, b) => Number(b?.data?.starRating || 0) - Number(a?.data?.starRating || 0)
      );
    }
  }

  const sort = useCallback((filters: any, turfData = filteredTurfData) => {
    const sortKey = filters.sortBy;

    switch (sortKey) {
      case SORT_KEYS.PRICE_HIGH_TO_LOW:
        return turfData.sort((a, b) => comparePrice(a, b, SortOrder.DESC));
      case SORT_KEYS.RATING_HIGH_TO_LOW:
        return sortByRating(turfData, SortOrder.DESC);
      case SORT_KEYS.RATING_LOW_TO_HIGH:
        return sortByRating(turfData, SortOrder.ASC);
      case SORT_KEYS.DISTANCE_NEAREST:
        return sortTurfByLocation(turfData, userLocation);
      case SORT_KEYS.TURF_HEIGHT:
        return turfData.sort((a, b) => compareHeight(a, b, SortOrder.DESC));
      default:
        return turfData.sort((a, b) => comparePrice(a, b, SortOrder.ASC));
    }
  }, [filteredTurfData, userLocation]);

  const filter = (filters: string[], turfData: TileCardDetails[]) => {
    let filteredData = [...turfData];

    filters?.forEach((filterKey) => {
      switch (filterKey) {
        case FILTERS_KEYS.COVERED:
          filteredData = filteredData.filter(
            ({ data }) => data.isRainProtected === true
          );
          break;

        case FILTERS_KEYS.OPEN_24_7:
          filteredData = filteredData.filter(
            ({ data }) => data.availabilityData?.isAvailable24Hours === true
          );
          break;

        case FILTERS_KEYS.CAFE:
          filteredData = filteredData.filter(
            ({ data }) => data.hasCafe === true
          );
          break;

        case FILTERS_KEYS.PARTNERED:
          filteredData = filteredData.filter(
            ({ data }) => data.partnership === true
          );
          break;
        case FILTERS_KEYS.UNCOVERED:
          filteredData = filteredData.filter(
            ({ data }) => data.isRainProtected === false
          );
          break;
      }
    });

    return filteredData;
  };
  // Size range filter disabled — was hiding turfs below SIZE_RANGE.MIN without UI control
  // const filterSizeRange = (range: number[], turfData: TileCardDetails[]) => {
  //   if (!range.length) {
  //     return turfData;
  //   }
  //   const filtered = turfData.filter((turf) => {
  //     const hasMatchingSize = turf.data.areaPrice.some((areaPrice) => {
  //       const turfArea = parseInt(areaPrice.area);
  //       const isInRange = turfArea >= range[0] && turfArea <= range[1];
  //       return isInRange;
  //     });
  //     return hasMatchingSize;
  //   });
  //   return filtered;
  // };

  const filterTurfsByDistanceRange = useCallback((
    distanceRange: number,
    turfData: TileCardDetails[]
  ) => {
    if (userLocation && distanceRange !== DISTANCE_RANGE_MAX) {
      const sortedTurfs = sortTurfByLocation(turfData, userLocation);

      const turfsInRange = sortedTurfs.filter((turf) => {
        // Add null checks for location property
        const location = turf?.data?.location;
        if (!location || !location.latitude || !location.longitude) {
          return false; // Exclude turfs without valid location data
        }
        
        const lat = Number(location.latitude);
        const lng = Number(location.longitude);
        
        // Check if coordinates are valid numbers
        if (isNaN(lat) || isNaN(lng)) {
          return false;
        }
        
        const distance = calculateDistance(
          userLocation.latitude,
          userLocation.longitude,
          lat,
          lng
        );
        return distance <= distanceRange;
      });
      return turfsInRange;
    } else {
      return turfData;
    }
  }, [userLocation]);

  // const onSearch = (search: string) => {
  //   setSearch(search);
  // };



  // Helper to sort exclusive turfs - wrapped in useCallback to prevent infinite loops
  const sortExclusiveTurf = useCallback((turfData: TileCardDetails[]) => {
    const turfDataCopy = [...turfData];
    const exclusiveTurfs: TileCardDetails[] = [];
    const regularTurfs: TileCardDetails[] = [];
    const unavailableTurfs: TileCardDetails[] = [];
    turfDataCopy.forEach((turf) => {
      // Skip hidden turfs - they should not be displayed at all
      if (turf.data.isTurfHidden) {
        return;
      }
      
      if (turf.data.exclusivePriority !== undefined) {
        const exclusiveTurf = {
          ...turf,
          data: {
            ...turf.data,
            exclusivePriority: turf.data.exclusivePriority
          }
        };
        exclusiveTurfs.push(exclusiveTurf);
      } else if (turf.data.isTurfUnavailable) {
        unavailableTurfs.push(turf);
      } else {
        regularTurfs.push(turf);
      }
    });
    exclusiveTurfs.sort(
      (a, b) =>
        (a.data.exclusivePriority ?? 0) - (b.data.exclusivePriority ?? 0)
    );
    return [...exclusiveTurfs, ...regularTurfs, ...unavailableTurfs];
  }, []);

  // Note: isMobile detection removed - using CSS media queries to prevent layout flash
  // Both mobile and desktop cards are rendered, visibility controlled by CSS classes

  // Handle filtering and processing data
  useEffect(() => {
    
    // Clear filtered data immediately when city changes or when loading
    if (isLoading) {
      setFilteredTurfData([]);
      setShowNoTurf(false);
      // Reset redirect flag when loading starts (allows redirect after load completes)
      if (hasRedirectedRef.current) {
        hasRedirectedRef.current = false;
      }
      return;
    }

    // Don't process if turfData is empty or null
    // BUT only check redirect if we've finished loading (isLoading is false)
    if ((!turfData || turfData.length === 0) && !isLoading) {
      setFilteredTurfData([]);
      setShowNoTurf(false);
      
      const currentPath = router.asPath || router.pathname;
      const isOnNoDataRoute = router.pathname === `/noData` || router.asPath === '/noData' || currentPath === '/noData';
      const isCityRoute = router.pathname?.includes('[city]');
      const isAreaRoute = currentPath.includes('/area/');
      
      if (
        !isOnNoDataRoute && 
        !isCityChanging && 
        !isAreaRoute &&
        city && 
        city !== 'noData' &&
        !hasRedirectedRef.current &&
        isCityRoute &&
        turfData !== undefined
      ) {
        hasRedirectedRef.current = true;
        router.replace('/noData');
      }
      return;
    }

    // If we're in a city change state and have data, clear the flag
    if (isCityChanging && turfData.length > 0) {
      setIsCityChanging(false);
    }

    // City pages already receive city-scoped turf data from SSR/ISR.
    // Re-filtering by raw city strings here causes renamed cities and
    // typo-ed source data (e.g. Ahmedabad/Ahemdabad) to disappear.
    const filteredCityData = Array.isArray(turfData) ? turfData : [];

    if (!filteredCityData.length) {
      setFilteredTurfData([]);
      setShowNoTurf(false);
      
      const isAreaRoutePath = (router.asPath || router.pathname).includes('/area/');
      if (router.pathname === `/noData` || isCityChanging || hasRedirectedRef.current || isAreaRoutePath) {
        return;
      }
      
      hasRedirectedRef.current = true;
      setTimeout(() => {
        router.push(`/noData`);
      }, 0);
      return;
    }

    // Use enhanced search filtering with address matching
    const searchTerm = typeof search === 'string' ? search : '';
    const searchFiltered = turfProcessor.filterBySearchWithAddress(filteredCityData, searchTerm);

    // Filter by selected area - only apply if areas exist for this city
    // and the selected area is valid for this city
    let areaFiltered = searchFiltered;
    const hasAreasForCity = areas && areas.length > 0;
    const normalizedSelectedArea = normalizeAreaForMatch(filters.selectedArea);
    const matchedSelectedArea = hasAreasForCity
      ? areas.find((area) => normalizeAreaForMatch(area.name) === normalizedSelectedArea)
      : null;
    const isValidSelectedArea = !!matchedSelectedArea;

    if (filters.selectedArea && isValidSelectedArea) {
      // Prefer areaId for filtering because area display names can differ
      // between areasByCity and turf.areaName (hyphen/space/canonical mismatches).
      const selectedAreaId = matchedSelectedArea?.id;
      areaFiltered = searchFiltered.filter((turf: TileCardDetails) => {
        const turfAreaId = turf.data.areaId || "";
        if (selectedAreaId && turfAreaId) {
          return turfAreaId === selectedAreaId;
        }
        // Fallback for data where areaId is missing
        return normalizeAreaForMatch(turf.data.areaName || "") === normalizedSelectedArea;
      });
    } else if (filters.selectedArea && hasAreasForCity && !isValidSelectedArea) {
      // Clear invalid selected area (e.g., from previous city session)
      setFilters((prev) => {
        if (prev.selectedArea === null) return prev;
        return {
          ...prev,
          selectedArea: null,
        };
      });
    }

    // Smart sorting: address-priority when searching, respects explicit sort otherwise
    const sortedData = sortProcessor.sortTurfsWithPriority(
      filters, areaFiltered, userLocation, searchTerm
    );
    const filteredAndSortedData = filterProcessor.filterTurfs(filters.filters ?? [], sortedData);
    // const filterSizeRage = filterSizeRange(
    //   filters.sizeRange ?? [],
    //   filteredAndSortedData
    // );
    const filterDistanceRange = turfProcessor.filterByDistance(
      filteredAndSortedData,
      userLocation,
      filters.distanceRange ?? DISTANCE_RANGE_MAX
    );
    const sortExclusiveTurfData = sortExclusiveTurf(filterDistanceRange);

    if (sortExclusiveTurfData.length === 0) {
      setShowNoTurf(true);
    } else {
      setShowNoTurf(false);
    }

    setFilteredTurfData(Array.isArray(sortExclusiveTurfData) ? sortExclusiveTurfData : []);
  }, [city, search, filters, turfData, isLoading, isCityChanging, userLocation, sortExclusiveTurf, areas]);


  // Combined useEffect to handle partnered turfs from API data only
  useEffect(() => {
    if (typeof window === "undefined") return;
    
    const localStorageCity = localStorage.getItem("selectedCity");
    if (!localStorageCity) {
      setVisible(true);
    }

    // The locationState atom already rehydrates from sessionStorage on first read.
    // Restoring it here too let this effect resurrect a location that
    // MobileLocationSelector had just cleared, which looped until React bailed out
    // with "Maximum update depth exceeded".

    if (!userLocation && !hasAttemptedLocationRef.current) {
      hasAttemptedLocationRef.current = true;
      getUserLocation(setUserLocation);
    }
  }, [userLocation, setUserLocation]);

  useEffect(() => {
    if (!!userLocation) {
      setVisible(false);
      // Set default sort to distance when location is available
      setFilters((prev) => {
        // Only set distance sort if no sort is currently selected
        if (!prev.sortBy) {
          return {
            ...prev,
            sortBy: SORT_KEYS.DISTANCE_NEAREST,
          };
        }
        return prev;
      });
    }
  }, [userLocation, setFilters]);

// Scroll: Save position on scroll
useEffect(() => {
  if (typeof window === "undefined") return;
  
  const handleScroll = () => {
    sessionStorage.setItem(router.pathname, window.scrollY.toString());
  };
  window.addEventListener("scroll", handleScroll);
  return () => {
    window.removeEventListener("scroll", handleScroll);
    sessionStorage.setItem(router.pathname, window.scrollY.toString());
  };
}, [router.pathname]);

// Scroll: Restore saved scroll position after data is rendered
useEffect(() => {
  if (typeof window === "undefined") return;
  if (!hasRestoredScroll && !isLoading && filteredTurfData.length > 0) {
    const savedPosition = sessionStorage.getItem(router.pathname);
    if (savedPosition) {
      const scrollTarget = parseInt(savedPosition, 10);

      const interval = setInterval(() => {
        const contentHeight = document.body.scrollHeight;

        // When enough height is rendered, restore scroll
        if (contentHeight > scrollTarget + 100 || document.readyState === "complete") {
          window.scrollTo(0, scrollTarget);
          clearInterval(interval);
          setHasRestoredScroll(true);
        }
      }, 100);

      // Stop trying after 5 seconds
      setTimeout(() => clearInterval(interval), 5000);
    } else {
      setHasRestoredScroll(true);
    }
  }
}, [isLoading, filteredTurfData.length, hasRestoredScroll, router.pathname]);

useEffect(() => {
  if (typeof window === "undefined") return;
  if (!isLoading && filteredTurfData.length > 0) {
    const selectedIndex = sessionStorage.getItem("selectedCardIndex");
    if (selectedIndex !== null) {
      // Use a timeout to ensure DOM is rendered
      setTimeout(() => {
        const card = document.querySelector(`[data-card-index="${selectedIndex}"]`);
        if (card) {
          card.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        sessionStorage.removeItem("selectedCardIndex");
      }, 100);
    }
  }
}, [isLoading, filteredTurfData.length]);


  const url = router.asPath || "";
  const BannerURL = [
    //  "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/tournaments/desktop/tcl2desktop.png",
     "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/b3-turf/desktop/1.png",
    "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/gt-turf-and-food-court/desktop/2.png",
    "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/sportzfy-turf/desktop/2.png",
    "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/regional-turfyard/desktop/1.png",
    "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/the-indore-oval/desktop/1.png",
    "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/the-legends-turf/desktop/legends-desk.png",
    "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/24x7-sports-club-and-turf/desktop/24*7-desk.png",
    "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/venus-sports-arena/desktop/venus-desk.png",
    "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/tims-turf-and-cafe/desktop/1.svg",
    "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/arb-turf-and-cafe/desktop/1.png",
    // "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/riders-turf/desktop/1.png",
    // "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/pavilion-turf-mahalaxmi-nagar/desktop/1.svg",
    "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/heavens-turf-and-cafe/desktop/1.svg",
    "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/worldcup-turf/desktop/1.svg",
    // "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/greenland-turf-and-cafe/desktop/1.png",
    // "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/all-rounder-turf/desktop/1.png",
    // "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/the-escobar-turf-arena/desktop/1.png",
    // "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/backYard-arena-turf/desktop/1.png",
    // "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/one-tip-turf/desktop/1.png",
    // "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/sp-turf-and-cafe/desktop/1.png",
    // "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/the-harbour-house-sports-cafe-and-turf/desktop/1.png",
    // "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/the-homeground-turf-and-cafe/desktop/1.png",
  ];
  const mobileBannerUrl = [
//   "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/tournaments/mobile/tcl2mobile.png",
  "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/b3-turf/mobile/1.png",
    "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/gt-turf-and-food-court/mobile/2.png",
    "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/sportzfy-turf/mobile/2.png",
    "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/regional-turfyard/mobile/1.png",
    "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/the-indore-oval/mobile/1.png",
    "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/the-legends-turf/mobile/legends-mob.png",
    "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/24x7-sports-club-and-turf/mobile/24*7-mob.png",
    "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/venus-sports-arena/mobile/venus-mob.png",
    "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/tims-turf-and-cafe/mobile/1.svg",
    "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/arb-turf-and-cafe/mobile/1.png",
    // "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/riders-turf/mobile/1.png",
    // "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/pavilion-turf-mahalaxmi-nagar/mobile/1.svg",
    "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/heavens-turf-and-cafe/mobile/1.svg",
    "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/worldcup-turf/mobile/1.svg",
    // "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/greenland-turf-and-cafe/mobile/1.png",
    // "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/all-rounder-turf/mobile/1.png",
    // "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/the-escobar-turf-arena/mobile/1.png",
    // "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/backYard-arena-turf/mobile/1.png",
    // "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/one-tip-turf/mobile/1.png",
    // "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/sp-turf-and-cafe/mobile/1.png",
    // "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/the-harbour-house-sports-cafe-and-turf/mobile/1.png",
    // "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/all_banners/Indore/the-homeground-turf-and-cafe/mobile/1.png",
  ];



  // Memoize the banner click handler to prevent re-renders
  const handleBannerClick = React.useCallback(
    (index: number, element: any) => {
      if (index === 0) {
        router.push("/tournament");
        return;
      }

      if (
        isValidElement(element) &&
        element.props &&
        typeof element.props === "object" &&
        "src" in element.props
      ) {
        const result = getTurfRouteFromBanner(element.props.src as string, turfData);
        
        if (result.isPavilion) {
          // Handle pavilion case - show modal with multiple locations
          const pavilionUsernames = ['pavilion-turf-mahalaxmi-nagar', 'pavilion-turf-nipania', 'pavilion-turf-dewas-naka'];
          const filtered = turfData.filter((turf) => 
            pavilionUsernames.some(name => turf.userName?.toLowerCase()?.includes(name.toLowerCase()))
          );
          setPavilionTurfs(filtered);
          setShowPavilionModal(true);
        } else if (result.url) {
          // Navigate to turf detail page using internal route
          router.push(result.url);
        }
      }
    },
    [turfData, router]
  );

  // SSR / pre-hydration: show a loading spinner to users while keeping real turf
  // card markup in the DOM for Google to crawl. The cards are visually hidden with
  // inline styles (no FOUC) but remain in the HTML source for SEO.
  if (!isHydrated || typeof window === "undefined") {
    const ssrTurfs = filterTurfsBySportTab(
      Array.isArray(turfData) ? turfData : [],
      activeTab
    );
    return (
      <>
        <div className="loader-container">
          <ClipLoader size={50} color="purple" loading={true} />
          <p>Loading...</p>
        </div>

        {/* SEO-only: real card markup hidden from users but crawlable by Google */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            width: "1px",
            height: "1px",
            padding: 0,
            margin: "-1px",
            overflow: "hidden",
            clip: "rect(0,0,0,0)",
            whiteSpace: "nowrap",
            border: 0,
          }}
        >
          {ssrTurfs.length > 0 ? (
            ssrTurfs.slice(0, 6).map((t, i) => (
              <div key={i}>
                <div className="mobile-only-card">
                  <MobileCard
                    {...t}
                    activeSport={activeSport}
                    city={city}
                    isUnavailable={t.data?.isTurfUnavailable || false}
                    index={i}
                    isLCP={i === 0}
                    distanceLabel={null}
                  />
                </div>
                <div className="desktop-only-card">
                  <Tile
                    {...t}
                    activeSport={activeSport}
                    city={city}
                    isUnavailable={t.data?.isTurfUnavailable || false}
                    index={i}
                    isLCP={i === 0}
                    distanceLabel={null}
                  />
                </div>
              </div>
            ))
          ) : (
            <div className="no-data">No Turf Found</div>
          )}
        </div>
      </>
    );
  }

  const shouldShowBanner =
    SHOW_TILE_CONTAINER_OFFER_BANNERS &&
    (city || "").toLowerCase() === constants.DEFAULT_CITY.toLowerCase();

  return (
    <>
      <SelectLocation
        setCity={setCity}
        visible={visible}
        onClose={() => setVisible(false)}
      />
      <FiltersInitializer />
      <div>
        <Row className="container">
          <Col xs={24}>
            {/* MobileLocationSelector - hidden on desktop via CSS */}
            <div className="mobile-only-card">
              <MobileLocationSelector />
            </div>
            
            {/* H1 for SEO - visually hidden but present for search engines */}
            <h1 className="sr-only" style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}>
              {`Book Cricket & Football Turfs in ${city ? city.charAt(0).toUpperCase() + city.slice(1) : "India"}`}
            </h1>
            
            {/* Banner carousel - only for Indore */}
            {shouldShowBanner && (
              <div className="carousel">
                {/* Mobile Banner Carousel - hidden on desktop via CSS */}
                <div className="mobile-only-card">
                  <Carousel
                    autoPlay={true}
                    infiniteLoop={true}
                    interval={2000}
                    stopOnHover={true}
                    showIndicators={false}
                    showThumbs={false}
                    onClickItem={handleBannerClick}
                    showArrows={true}
                    showStatus={false}
                    {...({} as any)}
                  >
                    {mobileBannerUrl.map((item, index) => (
                      <img
                        src={item}
                        key={`mobile-banner-${index}`}
                        alt={`Special offer on turf booking in ${city || 'India'} - Banner ${index + 1}`}
                        title={`Turf booking offer in ${city || 'India'}`}
                        width={768}
                        height={200}
                        className="offer-banner-img"
                        loading="lazy"
                        decoding="async"
                        fetchPriority={index === 0 ? "low" : "auto"}
                      />
                    ))}
                  </Carousel>
                </div>
                {/* Desktop Banner Carousel - hidden on mobile via CSS */}
                <div className="desktop-only-card">
                  <Carousel
                    autoPlay={true}
                    infiniteLoop={true}
                    interval={2000}
                    stopOnHover={true}
                    showIndicators={false}
                    showThumbs={false}
                    onClickItem={handleBannerClick}
                    showArrows={true}
                    showStatus={false}
                    {...({} as any)}
                  >
                    {BannerURL.map((item, index) => (
                      <img
                        src={item}
                        key={`desktop-banner-${index}`}
                        alt={`Special offer on turf booking in ${city || 'India'} - Banner ${index + 1}`}
                        title={`Turf booking offer in ${city || 'India'}`}
                        width={1200}
                        height={200}
                        className="offer-banner-img"
                        loading="lazy"
                        decoding="async"
                        fetchPriority={index === 0 ? "low" : "auto"}
                      />
                    ))}
                  </Carousel>
                </div>
                {/* Partnered turfs carousel for Indore (only if partnered turfs exist) */}
                
              </div>
            )}
            
          </Col>

          <Col xs={24} md={4} style={{ marginTop: shouldShowBanner ? 0 : 20 }}>
            <FilterSort setFilters={setFilters} filters={filters} activeTab={activeTab} handleTabChange={handleTabChange} areas={areas} />
            <div className="turf-count">
              {isLoading ? (
                <span>Loading...</span>
              ) : (
                <>
                  <span>{getFilteredDataForTab().length}</span>{" "}
                  {activeTab === 'turfs'
                    ? (getFilteredDataForTab().length === 1 ? "Turf found" : "Turf's found")
                    : activeTab === 'courts'
                    ? (getFilteredDataForTab().length === 1 ? "Pickleball Court found" : "Pickleball Courts found")
                    : activeTab === 'badminton'
                    ? (getFilteredDataForTab().length === 1 ? "Badminton Court found" : "Badminton Courts found")
                    : activeTab === 'football'
                    ? (getFilteredDataForTab().length === 1 ? "Football Turf found" : "Football Turfs found")
                    : "found"}
                </>
              )}
            </div>
          </Col>
          <Col xs={24} md={20}>
            <Row
              style={{
                margin: "0 1rem 3rem 1rem",
                marginTop: shouldShowBanner ? 0 : 20,
              }}
              align={"stretch"}
              gutter={[24, 24]}
              wrap={true}
            >
              {isLoading ? (
                <Col span={24} style={{ textAlign: 'center', padding: '2rem' }}>
                  <div style={{ fontSize: '16px', color: '#666' }}>
                    <ClipLoader size={30} color="purple" loading={true} />
                    <div style={{ marginTop: '10px' }}>Loading {activeTab === 'courts' ? 'pickleball courts' : activeTab === 'badminton' ? 'badminton courts' : activeTab === 'football' ? 'football turfs' : 'turfs'} ...</div>
                  </div>
                </Col>
              ) : (
                <>
                  {(() => {
                    const tabData = getFilteredDataForTab();
                    const noDataLabel = activeTab === 'courts' ? 'No Pickleball Court Found'
                      : activeTab === 'badminton' ? 'No Badminton Court Found'
                      : activeTab === 'football' ? 'No Football Turf Found'
                      : 'No Turf Found';
                    return (
                      <>
                        {tabData.slice(0, displayCount).map((turfData: any, i: number) => {
                          const isUnavailable = turfData.data?.isTurfUnavailable || false;
                          const distanceLabel = getTurfDistanceLabel(
                            userLocation,
                            turfData
                          );
                          return (
                            <Col
                              xl={8} lg={12} md={12} sm={12} xs={24}
                              key={i}
                              data-card-index={i}
                            >
                              <div className="mobile-only-card">
                                <MobileCard
                                  {...turfData}
                                  activeSport={activeSport}
                                  city={city}
                                  isUnavailable={isUnavailable}
                                  index={i}
                                  isLCP={i === 0}
                                  distanceLabel={distanceLabel}
                                />
                              </div>
                              <div className="desktop-only-card">
                                <Tile
                                  {...turfData}
                                  activeSport={activeSport}
                                  city={city}
                                  isUnavailable={isUnavailable}
                                  index={i}
                                  isLCP={i === 0}
                                  distanceLabel={distanceLabel}
                                />
                              </div>
                            </Col>
                          );
                        })}
                        {tabData.length === 0 && !isLoading && <div className="no-data">{noDataLabel}</div>}
                        {tabData.length > displayCount && (
                          <Col span={24} style={{ textAlign: 'center', padding: '20px' }}>
                            <div ref={loadMoreRef} style={{ height: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                              {isLoadingMore && <ClipLoader size={24} color="#7c3aed" />}
                            </div>
                          </Col>
                        )}
                      </>
                    );
                  })()}
                </>
              )}
            </Row>
          </Col>
        </Row>
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="whatsapp-button"
          aria-label="Chat with us on WhatsApp for turf booking inquiries"
          title="Chat with us on WhatsApp"
        >
          <div className="button-content">
            <WhatsAppIcon size={20} className="whatsapp-icon" />
            <span className="whatsapp__text">
              Chat With Us
              <span className="whatsapp-text__icon" aria-hidden="true">
                <ArrowRightIcon size={14} />
              </span>
            </span>
          </div>
        </a>
      </div>
      
      {/* Pavilion Turfs Drawer */}
      <PavilionTurfsDrawer
        visible={showPavilionModal}
        onClose={() => setShowPavilionModal(false)}
        turfData={pavilionTurfs}
      />
    </>
  );
};

export default TileContainer;
